import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BaseDbObject } from "../models/db";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "./utils";
import { UploadPresignedFile } from "../file";
import { PossibleRole } from "lhc_permissions";

const ENTITY_KEY = "users";

export const ClientUserFiltersValidation = z.object({
  page: z.number().min(1),
  search: z.string().optional(),
  enabled: z.boolean().optional(),
});

// Validation schema for updating user permissions
export const UpdateUserPermissionsValidation = z.object({
  user_id: z.string().uuid(),
  permissions: z.array(z.string()),
});

export const CreateUserValidation = z.object({
  phone_number: z.string(),
  name: z.string().min(1),
});

export const UpdateUserValidation = z.object({
  id: z.string().uuid(),
  phone_number: z.string(),
  name: z.string().min(1),
  enabled: z.boolean(),
});

export const DeleteUserValidation = z.object({
  id: z.string().uuid(),
});

export interface DbMobileUser extends BaseDbObject {
  name: string;
  phone_number: string;
  enabled: boolean;
}

function assembleUserQueryParams(filters: typeof ClientUserFiltersValidation._type) {
  const queryParams = new URLSearchParams();
  queryParams.append("page", `${filters.page}`)
  if (filters.search) {
    queryParams.append("search", filters.search)
  }
  if (filters.enabled) {
    queryParams.append("enabled", filters.enabled.toString())
  }
  return queryParams;
}

export function fetchClientUsersPage(filters: typeof ClientUserFiltersValidation._type) {
  const queryParams = assembleUserQueryParams(filters);

  return fetchApiGet<{
    data: DbMobileUser[]
    total: number;
    totalPages: number;
    pageSize: number;
    currentPage: number;
  }>(`client/users`, queryParams);
}

export function useClientUsersPaginated({ filters, queryFn }: {
  filters: typeof ClientUserFiltersValidation._type,
  queryFn: any,
}) {
  return useQuery({
    queryKey: ["mobile", "users", `${assembleUserQueryParams(filters).toString()}`],
    queryFn,
    retry: 1,
    cacheTime: 0,
    keepPreviousData: true,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return async (data: typeof CreateUserValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<DbMobileUser>(
      `client/users`,
      data,
      queryParams,
    );

    queryClient.invalidateQueries({ queryKey: ["mobile", "users"] });
    return result;
  };
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return async (data: typeof UpdateUserValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<DbMobileUser>(
      `client/users/${data.id}`,
      data,
      queryParams,
    );

    queryClient.invalidateQueries({ queryKey: ["mobile", "users"] });
    return result;
  };
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return async (data: typeof DeleteUserValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiDelete<DbMobileUser>(
      `client/users/${data.id}`,
    );

    queryClient.invalidateQueries({ queryKey: ["mobile", "users"] });
    return result;
  };
}

export function useListProviders({
  isUploadable,
  providerType,
}: {
  providerType?: string;
  isUploadable?: boolean;
}) {
  const queryParams = new URLSearchParams();
  if (providerType) {
    queryParams.append("providerType", providerType);
  }
  if (isUploadable) {
    queryParams.append("isUploadable", "true");
  }

  return useQuery({
    queryKey: [ENTITY_KEY],
    queryFn: () => fetchApiGet("providers", queryParams),
    retry: 1,
  });
}

export interface ClientResource {
  id: string;
  user_id: string;
  filename: string;
  display_name: string;
  file_type: string;
  is_enabled: boolean;
}

export interface CreateResourceResponse {
  resource: ClientResource;
  upload_url: string;
}

export interface UpdateResourceResponse {
  resource: ClientResource;
}

export const VALID_FILE_TYPES = [".pdf", ".png", ".jpeg", ".jpg", ".webp"] as const;
export type FileType = typeof VALID_FILE_TYPES[number];

export const CreateResourceValidation = z.object({
  filename: z.string().min(1),
  display_name: z.string().min(1),
  file_type: z.enum(VALID_FILE_TYPES),
  is_enabled: z.boolean(),
});

export const UpdateResourceValidation = z.object({
  id: z.string().uuid(),
  display_name: z.string().min(1),
  is_enabled: z.boolean(),
});

export function useCreateResource() {
  const queryClient = useQueryClient();

  return async (data: typeof CreateResourceValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<CreateResourceResponse>(
      `client/resources`,
      data,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["mobile", "resources"] });
    }

    return result;
  };
}

/**
 * Create a resource and upload a file in one operation
 * @param resourceData Resource metadata
 * @param file The file to upload
 * @returns A promise with the result of both operations
 */
export function useCreateResourceWithFile() {
  const createResource = useCreateResource();
  
  return async (resourceData: typeof CreateResourceValidation._type, file: File) => {
    try {
      // Step 1: Create the resource and get the upload URL
      const createResult = await createResource(resourceData);
      
      if (!createResult.success || !createResult.data?.upload_url) {
        return createResult;
      }
      
      // Step 2: Upload the file to the presigned URL
      const uploadResult = await UploadPresignedFile(
        createResult.data.upload_url, 
        file, 
        file.type
      );
      
      if (!uploadResult.success) {
        return {
          success: false,
          error: "File upload failed after resource creation",
          data: {
            resource: createResult.data.resource,
            uploadError: uploadResult.data
          }
        };
      }
      
      return {
        success: true,
        data: {
          resource: createResult.data.resource,
          upload: uploadResult.data
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };
}

export function useUpdateResource() {
  const queryClient = useQueryClient();

  return async (data: typeof UpdateResourceValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<UpdateResourceResponse>(
      `client/resources/${data.id}`,
      data,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["mobile", "resources"] });
    }

    return result;
  };
}

export function useGenerateResourceUrl() {
  const queryClient = useQueryClient();

  return async (data: { id: string }) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ link: string }>(
      `client/resources/${data.id}/url`,
      data,
      queryParams
    );

    return result;
  };
}

export function useListMobileResources() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["mobile", "resources", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<ClientResource[]>("client/resources", queryParams),
    retry: 2,
  });
}